import axios from 'axios'
import {createFullUrl, getAppOrigin} from '@/utils/urlUtils'
import {handleMrtSsrErrors} from './errorHandlerHelper'
import {ExpenseSummary} from '../components/expense-dashboard/expense-dashboard'

type ExpenseApiResponse = {
    isCustomerExists: boolean
    expenseTotal: number
    paidWithHSA: number
    reimbursementFromHSA: number
    eligibleReimburstment: number
}

export const calculatePercentage = (expenseAmount: number, expenseTotal: number): string => {
    if (expenseAmount === 0 || expenseTotal === 0) return '0.00%' // Prevent division by zero
    return `${((expenseAmount / expenseTotal) * 100).toFixed(2)}%`
}

export const fetchExpenseSummary = async (
    userEmail: string | null,
    hostURL?: {origin: string} | null
): Promise<ExpenseSummary> => {
    try {
        const urlPath = '/api/expense-dashboard-graph'
        const origin = hostURL?.origin || getAppOrigin() || ''
        const response = await axios.get(
            createFullUrl(origin, urlPath, {account_email: userEmail || ''})
        )

        const data: ExpenseApiResponse = response.data.data
        if (data && Object.keys(data).length > 0) {
            return {
                isCustomerExists: data.isCustomerExists,
                expenseTotal: data.expenseTotal,
                items: [
                    {
                        title: 'Paid with HSA',
                        className: 'js-hsaCardPaid',
                        barClassName: 'c-expenseDashboard__graph__bar--grey',
                        percent: calculatePercentage(data.paidWithHSA, data.expenseTotal),
                        value: data.paidWithHSA.toString(), // Explicitly convert to string
                        valueDisplay: `$${data.paidWithHSA.toFixed(2)}`, // Ensure proper formatting
                        color: '#B8B8B8'
                    },
                    {
                        title: 'Reimbursed from HSA',
                        className: 'js-hsaReimbursed',
                        barClassName: 'c-expenseDashboard__graph__bar--green',
                        percent: calculatePercentage(data.reimbursementFromHSA, data.expenseTotal),
                        value: data.reimbursementFromHSA.toString(), // Explicitly convert to string
                        valueDisplay: `$${data.reimbursementFromHSA.toFixed(2)}`, // Ensure proper formatting
                        color: '#009600'
                    },
                    {
                        title: 'Eligible for Reimbursement',
                        className: 'js-eligibleForReimbursement',
                        barClassName: 'c-expenseDashboard__graph__bar--red',
                        percent: calculatePercentage(data.eligibleReimburstment, data.expenseTotal),
                        value: data.eligibleReimburstment.toString(), // Explicitly convert to string
                        valueDisplay: `$${data.eligibleReimburstment.toFixed(2)}`, // Ensure proper formatting
                        color: '#CD3C5E'
                    }
                ]
            }
        } else {
            throw new Error('No data returned from the API')
        }
    } catch (error) {
        handleMrtSsrErrors(
            error,
            'Error fetching expense summary data for micro-app expense dashboard'
        )
        return Promise.reject(error) // Ensure the function rejects on error
    }
}
